import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { AlertTriangle, TrendingDown, TrendingUp, ShieldCheck, ChevronDown, ChevronUp, Gauge } from 'lucide-react';

const fmt = (h) => `${Number(h).toFixed(2).replace(/\.00$/, '')}u`;
const fmtRange = (b) => `${b.min != null ? fmt(b.min) : '—'}–${b.max != null ? fmt(b.max) : '—'}`;

function BudgetBanner({ budget }) {
  if (!budget || !budget.enabled) return null;
  const { status, total_planned, over_by, under_by } = budget;
  if (status === 'over' || status === 'under') {
    const over = status === 'over';
    return (
      <div
        data-testid={`roster-budget-${status}`}
        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${over ? 'border-rose-300 bg-rose-50 text-rose-800' : 'border-amber-300 bg-amber-50 text-amber-900'}`}
      >
        <AlertTriangle size={15} />
        <span>
          <strong>{over ? 'Boven uren-budget' : 'Onder uren-budget'}:</strong> gepland {fmt(total_planned)} · budget {fmtRange(budget)}
          <span className="ml-1 font-semibold">({over ? `+${fmt(over_by)} boven max` : `${fmt(under_by)} onder min`})</span>
        </span>
      </div>
    );
  }
  return (
    <div data-testid="roster-budget-ok" className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
      <Gauge size={15} />
      <span>Binnen uren-budget: gepland {fmt(total_planned)} · budget {fmtRange(budget)}</span>
    </div>
  );
}

export default function RosterGuardPanel({ locationSlug, year, week }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    if (!locationSlug) return;
    setLoading(true);
    try {
      const { data } = await api.get('/roster-guard', { params: { location_id: locationSlug, year, week } });
      setData(data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [locationSlug, year, week]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return null;
  const budget = data.budget;
  const flags = data.enabled ? (data.flags || []) : [];
  const budgetActive = budget && budget.enabled;

  // Nothing to show: per-employee guard off (or clean) AND no budget configured.
  if (!data.enabled && !budgetActive) return null;

  return (
    <div className="space-y-2" data-testid="roster-guard-wrapper">
      <BudgetBanner budget={budget} />

      {data.enabled && flags.length === 0 && (
        <div data-testid="roster-guard-ok" className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          <ShieldCheck size={15} />
          <span>Uren-guard: alle vaste medewerkers binnen de bandbreedte deze week.</span>
        </div>
      )}

      {data.enabled && flags.length > 0 && (
        <div data-testid="roster-guard-panel" className="rounded-lg border border-amber-300 bg-amber-50/60 overflow-hidden">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="roster-guard-toggle"
            className="flex w-full items-center justify-between px-3 py-2.5 text-left"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <AlertTriangle size={16} />
              Uren-guard — {flags.length} medewerker(s) buiten bandbreedte
              <span className="ml-1 inline-flex gap-1.5 text-xs font-normal">
                {data.summary.onderbezet > 0 && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-rose-700">{data.summary.onderbezet} onderbezet</span>}
                {data.summary.overbezet > 0 && <span className="rounded bg-orange-100 px-1.5 py-0.5 text-orange-700">{data.summary.overbezet} overbezet</span>}
              </span>
            </span>
            {open ? <ChevronUp size={16} className="text-amber-800" /> : <ChevronDown size={16} className="text-amber-800" />}
          </button>
          {open && (
            <ul className="divide-y divide-amber-200/70 border-t border-amber-200" data-testid="roster-guard-list">
              {flags.map((f) => {
                const under = f.direction === 'onderbezet';
                return (
                  <li key={f.employee_id} data-testid={`roster-guard-flag-${f.employee_id}`} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full ${under ? 'bg-rose-100 text-rose-700' : 'bg-orange-100 text-orange-700'}`}>
                        {under ? <TrendingDown size={13} /> : <TrendingUp size={13} />}
                      </span>
                      <span className="truncate font-medium text-slate-800">{f.name}</span>
                      <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${under ? 'bg-rose-100 text-rose-700' : 'bg-orange-100 text-orange-700'}`}>
                        {under ? 'Onderbezet' : 'Overbezet'}
                      </span>
                    </div>
                    <div className="text-xs tabular-nums text-slate-600 whitespace-nowrap">
                      gepland <strong className="text-slate-900">{fmt(f.planned_hours)}</strong> / contract {fmt(f.contract_hours)}
                      <span className={under ? 'text-rose-600 ml-1' : 'text-orange-600 ml-1'}>({f.delta_hours > 0 ? '+' : ''}{fmt(f.delta_hours)})</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
